/**
 * Seguimento das propostas no Odoo — o lado do ecrã. Os tipos espelham
 * `functions/lib/odoo/followup.ts` (outro tsconfig, não importável daqui);
 * `buildFollowupReport` agrega a secção dos Relatórios por comercial.
 */
import type { AppShell } from "@/App"

export type FollowupState = "none" | "pending" | "active" | "closed" | "unknown"

export const FOLLOWUP_LABEL: Record<FollowupState, string> = {
  none: "Sem seguimento",
  pending: "Pendente",
  active: "Em curso",
  closed: "Fechada",
  unknown: "Lead do Odoo não encontrada",
}

/** Cor do selo (variantes do Badge). */
export const FOLLOWUP_VARIANT: Record<FollowupState, "destructive" | "warning" | "info" | "success" | "neutral"> = {
  none: "destructive",
  pending: "warning",
  active: "info",
  closed: "success",
  unknown: "neutral",
}

export interface FollowupAction {
  at: string
  text: string
  author: string | null
  source: "odoo" | "app"
}

export interface FollowupActivity {
  summary: string
  type: string | null
  deadline: string | null
  user: string | null
  overdue: boolean
}

export interface Followup {
  proposal_id: string
  lead_id: number
  state: FollowupState
  due_at: string | null
  overdue_days: number
  first_action_at: string | null
  last_action: FollowupAction | null
  on_time: boolean | null
  stage_name: string | null
  outcome: "won" | "lost" | null
  /** O chatter da lead passa o limite de uma leitura: o estado pode estar incompleto. */
  messages_truncated?: boolean
  salesperson: { id: number; name: string } | null
  open_activities: FollowupActivity[]
  odoo_url: string | null
}

export interface FollowupReportRow {
  id: string
  number: string
  kind: string
  status: string
  client_name: string
  created_at: string
  seller_id: string | null
  seller_name: string | null
  followup: Followup
}

export interface FollowupReportData {
  rows: FollowupReportRow[]
  without_lead: number
  truncated: number
  hours: number
}

type Res<T> = { success: boolean; data?: T; error?: string }

function unwrap<T>(res: Res<T>, what: string): T {
  if (!res.success || !res.data) throw new Error(res.error || what)
  return res.data
}

export async function getFollowupReport(
  shell: AppShell,
  f: { from: string; to: string; kind?: string | null; seller?: string | null; generated_by: "ai" | "user" | "all" },
): Promise<FollowupReportData> {
  const q = new URLSearchParams({ from: f.from, to: f.to, generated_by: f.generated_by })
  if (f.kind) q.set("kind", f.kind)
  if (f.seller) q.set("seller", f.seller)
  return unwrap(await shell.client.get<Res<FollowupReportData>>(`reporting/followup?${q}`), "Falha a ler o seguimento no Odoo")
}

export interface FollowupSellerLine {
  key: string
  name: string
  received: number
  none: number
  pending: number
  active: number
  closed: number
  unknown: number
  /** Horas médias da criação à 1.ª ação (das que tiveram ação). */
  avgHoursToFirst: number | null
  /** Tratadas dentro do prazo / (tratadas + sem seguimento). */
  onTime: number | null
  onTimeDen: number
  onTimeNum: number
}

const NO_SELLER = "__none__"

function blank(key: string, name: string): FollowupSellerLine {
  return { key, name, received: 0, none: 0, pending: 0, active: 0, closed: 0, unknown: 0, avgHoursToFirst: null, onTime: null, onTimeDen: 0, onTimeNum: 0 }
}

export interface FollowupReport {
  bySeller: FollowupSellerLine[]
  totals: FollowupSellerLine
  /** «Sem seguimento», as mais atrasadas primeiro. */
  overdue: FollowupReportRow[]
}

/**
 * Por comercial (o vendedor da lead no Odoo): recebidas · sem seguimento ·
 * pendentes · em curso · fechadas · tempo médio até à 1.ª ação · % dentro do
 * prazo. O «dentro do prazo» conta as que já se decidiram: com ação (a tempo
 * ou não) e as sem seguimento; as pendentes ainda não contam.
 */
export function buildFollowupReport(rows: readonly FollowupReportRow[]): FollowupReport {
  const lines = new Map<string, FollowupSellerLine>()
  const totals = blank("__total__", "Total")
  const hours = new Map<string, number[]>()
  for (const r of rows) {
    const f = r.followup
    const key = f.salesperson ? String(f.salesperson.id) : NO_SELLER
    const line = lines.get(key) ?? blank(key, f.salesperson?.name ?? "Sem comercial no Odoo")
    lines.set(key, line)
    for (const l of [line, totals]) {
      l.received += 1
      l[f.state] += 1
      if (f.on_time !== null || f.state === "none") {
        l.onTimeDen += 1
        if (f.on_time === true) l.onTimeNum += 1
      }
    }
    if (f.first_action_at) {
      const h = (Date.parse(f.first_action_at) - Date.parse(r.created_at)) / 3_600_000
      if (Number.isFinite(h) && h >= 0) {
        for (const k of [key, totals.key]) hours.set(k, [...(hours.get(k) ?? []), h])
      }
    }
  }
  const finish = (l: FollowupSellerLine) => {
    const hs = hours.get(l.key) ?? []
    l.avgHoursToFirst = hs.length ? hs.reduce((a, b) => a + b, 0) / hs.length : null
    l.onTime = l.onTimeDen ? l.onTimeNum / l.onTimeDen : null
    return l
  }
  const bySeller = Array.from(lines.values()).map(finish).sort((a, b) => b.none - a.none || b.received - a.received || a.name.localeCompare(b.name, "pt"))
  return {
    bySeller,
    totals: finish(totals),
    overdue: rows.filter((r) => r.followup.state === "none").sort((a, b) => b.followup.overdue_days - a.followup.overdue_days || a.created_at.localeCompare(b.created_at)),
  }
}

/** «2 h», «1 d 4 h». */
export function formatHours(h: number | null): string {
  if (h == null) return "—"
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`
  if (h < 24) return `${Math.round(h)} h`
  const d = Math.floor(h / 24)
  const rest = Math.round(h - d * 24)
  return rest ? `${d} d ${rest} h` : `${d} d`
}

/** Seguimento de um lote de propostas (lista: a página visível; detalhe: uma). */
export async function getFollowups(shell: AppShell, ids: readonly string[]): Promise<Record<string, Followup>> {
  if (ids.length === 0) return {}
  const res = await shell.client.get<Res<{ items: Record<string, Followup> }>>(`followup?ids=${ids.map(encodeURIComponent).join(",")}`)
  return unwrap(res, "Falha a ler o seguimento no Odoo").items
}

